import { Lasso, Layers, MousePointer2, Scan } from "lucide-react";
import { Image as ImageIcon } from "lucide-react";
import { useRecoilState, useRecoilValue, useResetRecoilState } from "recoil";
import { projectConfigState, projectInfoState, selectedImageState } from "../../recoil";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  epochResultsState,
  heatmapModelState,
  heatmapModuleMsgState,
  heatmapModuleState,
  imageDataState,
  imageViewModeState,
  myAIModuleState,
  processImageSrcState,
  scoreMapImageState,
  trainingStateAtom,
  trainUuidState,
} from "../../recoil/upgrade";
import { useHeatmapModuleUser } from "../../hooks/useHeatmapModel";
import { Box, Button, CircularProgress, Tooltip, Typography } from "@mui/material";
import {
  simplifySegment,
  toSegmentFeatures,
  useImagePrepare,
  useLabelFilter,
  useLassoDraw,
  useSavedSegments,
  useSegmentPersistence,
  useSegmentSelection,
} from "common_ui_lib";
import { clickSegment, DraftItem, getPathImage, LabeledSegment, prepareImage, saveSegments, Segment } from "common-lib";
import { clearImgData, sendImgDataFromStorage, updateAIModules } from "common-lib";
import ScoremapOverlay from "../train/ScoremapOverlay";
import { filterSettingState } from "../../recoil/filterAtoms";
import { useFilteredImage } from "../../hooks/useFilteredImage";
import {
  LabelPopover,
  PolygonOverlay,
  EditableVertexOverlay,
  LabelFilterSelect,
  LassoDrawOverlay,
  MagnifierLens,
  type MagnifierLensHandle,
  SelectionHighlight,
  toNaturalCoords,
} from "common_ui_lib";

import LabelButton from "../label/LabelButton";
import { CustomToggle } from "../customComponent/CustomToggle";
import { useProjectLabelClasses } from "../../hooks/segment/useProjectLabelClasses";
import { getImageContour } from "../../services/projectService";
import { useParams } from "react-router-dom";
import { useInspImageContext } from "../../context/InspImageProvider";
import { pickLabelScores } from "../../hooks/useInspImage";
import LabelScorePanel from "./LabelScorePanel";
import { useTutorial } from "../../context/TutorialContext";
import { TUTORIALS } from "../../tutorial";
import { t } from "i18next";

export interface ScoremapOverlayProps {
  scoremapImages: string[];
  baseImage?: string;
  blend?: "max" | "avg" | "add";
  opacity?: number;

  cmap?: string;
  cmapShades?: number;
  cmapReverse?: boolean;
  cmapAlpha?: number;

  width?: number;
  height?: number;
  enableZoom?: boolean;
  zoom?: number;

  lens?: boolean;
  lensSize?: number;
  lensBorder?: string;
  lensShadow?: string;
  allowOverflow?: boolean;
  lensContentOpacity?: number;
  lensBlendMode?: string;
  lensSource?: "composite" | "base" | "overlay";
  zoomMode?: "on" | "off" | "lens";
}

interface ImagePartProps {
  isFilterOn: boolean;
  isOpenOption: boolean;
  isMulti: boolean;
}

type ToolMode = "select" | "lasso"; // select: 기존 클릭 선택/스캔, lasso: 자유 드래그 그리기

export default function ImagePart({ isFilterOn, isOpenOption, isMulti }: ImagePartProps) {
  const { id } = useParams();
  const Overlay = ScoremapOverlay as React.FC<ScoremapOverlayProps>;
  const myAIModule = useRecoilValue(myAIModuleState);
  const selectedRow = useRecoilValue(selectedImageState);
  const trainUuid = useRecoilValue(trainUuidState);
  const [imageData, setImageData] = useRecoilState(imageDataState);
  const viewMode = useRecoilValue(imageViewModeState);
  const canvasRef = useRef<HTMLCanvasElement | null>(null); //opencv
  const [isOpenCVLoaded, setIsOpenCVLoaded] = useState(true); //opencv가 로딩되는데 약간의 시간필요
  const projectData = useRecoilValue(projectInfoState);
  const [scoreMapImage, setScoreMapImage] = useRecoilState(scoreMapImageState);
  const [imageViewMode, setImageViewMode] = useRecoilState(imageViewModeState);
  const projectConfig = useRecoilValue(projectConfigState);
  const trainingState = useRecoilValue(trainingStateAtom);
  const epochResults = useRecoilValue(epochResultsState);
  const isTraining = !!trainingState[Number(id)];
  const connectedHeatmapModel = useRecoilValue(heatmapModelState);
  // 분석(멀티)은 클래스별 spec이 추론하므로 기존 경로를 그대로 쓴다
  const heatmapModel = isMulti ? null : connectedHeatmapModel;
  const useHeatmapSpec = imageViewMode === "scoremap" && !!heatmapModel;
  const isValidating = heatmapModel?.source === "epoch" && isTraining && epochResults.some((r) => r.state === "valid");
  useHeatmapModuleUser(useHeatmapSpec);
  const loadedHeatmapModule = useRecoilValue(heatmapModuleState);
  const heatmapModuleMsg = useRecoilValue(heatmapModuleMsgState);

  const heatmapModule = loadedHeatmapModule?.modelUuid === heatmapModel?.uuid ? loadedHeatmapModule : null;
  const [openOrgImage, setOpenOrgImage] = useState(true);
  const filterSetting = useRecoilValue(filterSettingState);
  const filteredSrc = useFilteredImage(imageData, filterSetting);

  const labelButtonRefs = useRef<Array<(() => void) | null>>([]);

  const imageBoxRef = useRef<HTMLDivElement | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);

  const [natural, setNatural] = useState<{ w: number; h: number } | null>(null);
  const [currentContours, setCurrentContours] = useState<string | null>(null);

  const { savedSegments, setSavedSegments } = useSavedSegments({ id: selectedRow.id, contours: currentContours });
  const { draftItems, setDraftItems, selectedIds, setSelectedIds, findHitId, editingTarget } =
    useSegmentSelection(savedSegments);
  const { saveStatus, setSaveStatus } = useSegmentPersistence(selectedRow?.id);
  // common_ui_lib의 persistSegments()도 위 prepareImage와 같은 원인(내부에 별도로 물려있는
  // common-lib 인스턴스의 client가 초기화 안 됨)으로 항상 실패해서, saveSegments를
  // 직접 호출한다. common_ui_lib 쪽 버그 수정 후 persistSegments로 되돌릴 것.
  const persistSegments = useCallback(
    async (segments: LabeledSegment[]) => {
      const rowId = selectedRow?.id;
      if (!rowId) return;
      // 라벨 없는 draft는 저장 대상에서 제외하되, 삭제로 인해 결과가 빈 배열이 되는 것도
      // 유효한 저장 상태이므로(전부 삭제) 여기서 return하지 않는다.
      const validSegments = segments.filter((s) => s.label?.trim());
      const features = toSegmentFeatures(validSegments);
      setSaveStatus("saving");
      try {
        await saveSegments(rowId, features);
        setSaveStatus("saved");
        setTimeout(() => setSaveStatus("idle"), 2000);
      } catch (error) {
        console.warn("세그먼트 저장 실패:", error);
        setSaveStatus("error");
      }
    },
    [selectedRow?.id, setSaveStatus],
  );
  const { activeLabelFilter, setActiveLabelFilter, uniqueLabels, labelCounts, visibleSavedSegments } =
    useLabelFilter(savedSegments);
  const { prepStatus, setPrepStatus } = useImagePrepare(filteredSrc ?? imageData);
  // common_ui_lib의 ensurePrepared()가 내부 토큰 캐시 버그로 항상 null을 반환해서
  // (prepareImage 자체는 성공하는데 결과가 버려짐), prepareImage를 직접 호출하고
  // 여기서 캐시한다. common_ui_lib 쪽 버그 수정 후 ensurePrepared로 되돌릴 것.
  const preparedRef = useRef<{ src: string; id: string } | null>(null);

  const [pendingLabel, setPendingLabel] = useState<{
    anchor: { x: number; y: number };
    targetIds: string[];
  } | null>(null);
  const makeId = () => crypto.randomUUID?.() ?? String(Date.now() + Math.random());
  const { originImage, scoremap, processImage, loading, error, visibleIndex, setVisibleIndex, fetchAiImgData, data } =
    useInspImageContext();
  const { labels, scoreList, thresholdList } = useMemo(() => pickLabelScores(data), [data]);
  const { run, tutorialKey, stepIndexRef, nextSubStep } = useTutorial();

  useEffect(() => {
    // AI 응답 전(originImage="")에 비우면 파일 서버에서 이미 받아둔 원본까지 사라져 느리게 뜬다
    if (originImage) setImageData(originImage);
    if (scoremap) {
      if (visibleIndex === null) {
        if (scoremap.length > 0) {
          setVisibleIndex(0);
          setScoreMapImage([scoremap[scoremap.length - 1]]);
        }
      } else if (scoremap[visibleIndex]) {
        setScoreMapImage([scoremap[visibleIndex]]);
      }
    }
  }, [visibleIndex, scoremap]);

  const [toolMode, setToolMode] = useState<ToolMode>("select");
  const { drawingPoints, handlePointerDown, handlePointerMove, handlePointerUp } = useLassoDraw(
    imgRef.current,
    (ring, additive) => {
      const simplified = simplifySegment([ring], 1.5)[0];
      const newItem: DraftItem = { id: makeId(), polygons: [simplified] };
      addDraftAndSelect(newItem, additive);
    },
  );

  const [magnifierEnabled, setMagnifierEnabled] = useState(false);
  const lensRef = useRef<MagnifierLensHandle | null>(null);
  const [boxDims, setBoxDims] = useState({ w: 600, h: 600 });

  // TODO: 이미지 hover시 정보 확인
  const [hoverInfo, setHoverInfo] = useState<{ label: string; clientPos: { x: number; y: number } } | null>(null);
  const { projectClasses, registerLabelClass } = useProjectLabelClasses();

  // 같은 AI 요청 반복 / 이미지마다 모듈 sync 하는 것을 막는다
  const aiReqKeyRef = useRef<string | null>(null);
  const syncedModuleRef = useRef<string | null>(null);

  // 모듈을 새로 올리면(모델 교체, 히트맵 재진입 등) AI 서버 옵션이 기본값으로 돌아간다
  // → import 직후에는 반드시 sync부터 다시 하도록 기록을 비운다
  useEffect(() => {
    syncedModuleRef.current = null;
    aiReqKeyRef.current = null;
  }, [loadedHeatmapModule]);

  const imageIdRef = useRef<string | null>(null);
  const prepTokenRef = useRef(0);

  const [isImageLoading, setIsImageLoading] = useState(false);
  const [siteOption, setSiteOption] = useState(null);
  // ponytail: setSize를 부르는 곳이 없어 사실상 상수. 창 크기에 맞춰야 하면 여기서 ResizeObserver로 올릴 것
  const size = 600;

  const handleKeyPress = useCallback((e: KeyboardEvent) => {
    const key = e.key;
    const index = Number(key) - 1;
    if (index >= 0 && index < labelButtonRefs.current.length) {
      labelButtonRefs.current[index]?.();
    }
  }, []);

  const addDraftAndSelect = useCallback((newItem: DraftItem, additive: boolean) => {
    setDraftItems((prev) => (additive ? [...prev, newItem] : [newItem]));
    setSelectedIds((prev) => {
      const next = additive ? new Set(prev) : new Set<string>();
      next.add(newItem.id);
      return next;
    });
  }, []);

  useEffect(() => {
    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [handleKeyPress]);

  useEffect(() => {
    fetch("/config.json")
      .then((res) => res.json())
      .then((config) => setSiteOption(config.SITE_OPTION));
  }, []);

  const applyOpenCVFilter = async (imageElement: HTMLImageElement) => {
    // if (!isOpenCVLoaded || !filterSetting.opencvFilter) return;
  };

  const fetchUpdateModule = async () => {
    const target = heatmapModule ?? myAIModule;
    if (!target) return;
    try {
      const data = {
        train_id: heatmapModule?.specId ?? trainUuid,
        site: siteOption,
        type: imageViewMode === "scoremap" ? "scoremap" : "preview",
        total: projectData?.counts.total_count,
        trained_count: 0,
        is_complete: false,
      };

      const updateResult = await updateAIModules(target.serverAddress, data, target);
      if (!updateResult) {
        throw new Error("AI 모듈 업데이트에 실패했습니다.");
      }
    } catch (error: any) {
      console.error(error);
    }
  };

  const fetchImage = async () => {
    let response: string;

    try {
      response = await getPathImage(selectedRow.path);
      setImageData(response);
    } catch (error) {
      console.error("이미지 로드 실패:", error);
      return;
    }

    // 검사 프로젝트도 행 id가 projectImageLabel.id로 매핑돼 있으므로 단계와 무관하게 조회.
    // id가 없으면(매칭 실패) 이전 이미지 컨투어가 남지 않도록 비움
    if (!selectedRow.id) {
      setCurrentContours(null);
    } else {
      try {
        const contours = await getImageContour(Number(id), selectedRow.id);
        setCurrentContours(contours.contours ?? null);
      } catch (error) {
        console.warn("컨투어 로드 실패 (이미지는 그대로 표시):", error);
        setCurrentContours(null);
      }
    }

    const img = new Image();

    img.onload = async () => {
      if (!canvasRef.current || !imageBoxRef.current) return;

      const canvas = canvasRef.current;
      const box = imageBoxRef.current;

      canvas.width = box.clientWidth;
      canvas.height = box.clientHeight;

      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      if (myAIModule && isFilterOn && isOpenCVLoaded) {
        try {
          await applyOpenCVFilter(img);
          return;
        } catch (error) {
          console.error("필터 적용 실패, 원본 이미지로 표시:", error);
        }
      }

      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    };

    img.onerror = () => console.error("이미지 디코딩 실패:", selectedRow.path);

    img.src = response;
  };

  useEffect(() => {
    if (window.cv) {
      setIsOpenCVLoaded(true);
    } else {
      window.onOpenCvReady = () => {
        setIsOpenCVLoaded(true);
      };
    }
  }, []);

  useEffect(() => {
    if (selectedRow?.path) fetchImage();
  }, [selectedRow?.path, imageViewMode]);

  useEffect(() => {
    if (!myAIModule || !selectedRow?.path || imageViewMode === "original") {
      return;
    }

    // 히트맵을 뽑을 모델이 없으면 요청하지 않는다 (안내만 띄운다)
    if (!isMulti && imageViewMode === "scoremap" && !heatmapModel) {
      return;
    }

    // 히트맵은 모델이 AI 서버에 올라오고, (학습 중이면) 검증이 끝난 뒤에 요청한다
    if (useHeatmapSpec && (!heatmapModule || isValidating)) {
      return;
    }

    const specId = heatmapModule?.specId;
    const modelKey = `${specId ?? trainUuid}|${heatmapModel?.uuid ?? ""}`;
    const reqKey = `${selectedRow.path}|${imageViewMode}|${modelKey}|${isMulti}|${viewMode}`;
    if (aiReqKeyRef.current === reqKey) return;
    aiReqKeyRef.current = reqKey;

    (async () => {
      const syncKey = `${modelKey}|${imageViewMode}`;
      if (syncedModuleRef.current !== syncKey) {
        await fetchUpdateModule();
        syncedModuleRef.current = syncKey;
      }
      fetchAiImgData({
        path: selectedRow.path,
        imageViewMode,
        isMulti,
        viewMode,
        specId,
      });
    })();
  }, [
    imageViewMode,
    selectedRow?.path,
    isMulti,
    myAIModule,
    viewMode,
    heatmapModule,
    useHeatmapSpec,
    isValidating,
    heatmapModel,
  ]);

  const handleShowRawImage = () => {
    setOpenOrgImage(!openOrgImage);
  };

  useEffect(() => {
    prepTokenRef.current++;
    imageIdRef.current = null;
    setPrepStatus("idle");

    setDraftItems([]);
    setSelectedIds(new Set());
    setPendingLabel(null);
  }, [imageData]);

  const handleVertexDragEnd = () => {
    if (!editingTarget || editingTarget.source !== "saved") {
      return;
    }
    persistSegments(savedSegments);
  };

  const editingPolygons =
    editingTarget?.source === "draft"
      ? draftItems.find((d) => d.id === editingTarget.id)?.polygons
      : editingTarget?.source === "saved"
        ? savedSegments.find((s) => s.id === editingTarget.id)?.polygons
        : null;

  const handlePolygonHover = (label: string | null, clientPos: { x: number; y: number } | null) => {
    if (label && clientPos) {
      setHoverInfo({ label, clientPos });
    } else {
      setHoverInfo(null);
    }
  };

  const onImageClick = async (e: React.MouseEvent) => {
    const pt = toNaturalCoords(e, imgRef.current);
    if (!pt) return;
    const additive = e.ctrlKey || e.metaKey;

    const hitId = findHitId(pt.x, pt.y);

    if (hitId) {
      setSelectedIds((prev) => {
        let next: Set<string>;
        if (additive) {
          next = new Set(prev);
          next.has(hitId) ? next.delete(hitId) : next.add(hitId);
        } else {
          next = prev.size === 1 && prev.has(hitId) ? new Set() : new Set([hitId]);
        }
        setDraftItems((draftPrev) => draftPrev.filter((d) => next.has(d.id)));
        return next;
      });
      return;
    }

    if (!additive) {
      setSelectedIds(new Set());
      setDraftItems([]);
    }

    const currentSrc = filteredSrc ?? imageData;
    if (!currentSrc) return;

    const cached = preparedRef.current;
    let id: string;
    if (cached && cached.src === currentSrc) {
      id = cached.id;
    } else {
      setPrepStatus("preparing");
      try {
        id = await prepareImage(currentSrc);
        preparedRef.current = { src: currentSrc, id };
        setPrepStatus("ready");
      } catch (error) {
        console.warn("이미지 준비 실패:", error);
        setPrepStatus("error");
        return;
      }
    }
    const res = await clickSegment(id, pt.x, pt.y);
    if (!res?.polygons?.length) return;

    const newItem: DraftItem = { id: makeId(), polygons: res.polygons };
    addDraftAndSelect(newItem, additive);

    if (run) {
      const currentSubStep = TUTORIALS[tutorialKey][stepIndexRef.current];
      if (currentSubStep?.nextTrigger === "contour-scan-complete") {
        nextSubStep();
      }
    }
  };

  const onImageContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    const pt = toNaturalCoords(e, imgRef.current);
    if (!pt) return;

    const rect = imageBoxRef.current?.getBoundingClientRect();
    const anchor = rect ? { x: e.clientX - rect.left, y: e.clientY - rect.top } : { x: 0, y: 0 };

    const onVertexHandle = e.target instanceof SVGElement;
    const hitId = findHitId(pt.x, pt.y) ?? (onVertexHandle ? editingTarget?.id : undefined);
    if (!hitId) return;

    const targetIds = selectedIds.has(hitId) && selectedIds.size > 0 ? Array.from(selectedIds) : [hitId];

    if (!(selectedIds.has(hitId) && selectedIds.size > 0)) {
      setSelectedIds(new Set([hitId]));
    }

    setPendingLabel({ anchor, targetIds });

    if (run) {
      const currentSubStep = TUTORIALS[tutorialKey][stepIndexRef.current];
      if (currentSubStep?.nextTrigger === "contour-label-start") {
        nextSubStep();
      }
    }
  };

  const commitLabel = async (label: string) => {
    if (!pendingLabel) return;

    const idSet = new Set(pendingLabel.targetIds);

    const promoted: LabeledSegment[] = draftItems
      .filter((d) => idSet.has(d.id))
      .map((d) => ({ id: d.id, polygons: d.polygons, label }));

    const nextSavedSegments = [...savedSegments.map((s) => (idSet.has(s.id) ? { ...s, label } : s)), ...promoted];

    setSavedSegments(nextSavedSegments);
    setDraftItems((prev) => prev.filter((d) => !idSet.has(d.id)));
    setSelectedIds(new Set());
    setPendingLabel(null);

    // TODO: 라벨 추가 시 프로젝트의 클래스 추가 후 데이터셋 파일 관련 로직 처리 필요
    // registerLabelClass(label);
    await persistSegments(nextSavedSegments);

    if (run) {
      const currentSubStep = TUTORIALS[tutorialKey][stepIndexRef.current];
      if (currentSubStep?.nextTrigger === "contour-label-saved") {
        nextSubStep();
      }
    }
  };

  const deleteLabel = async () => {
    if (!pendingLabel) return;
    const idSet = new Set(pendingLabel.targetIds);

    const nextSavedSegments = savedSegments.filter((s) => !idSet.has(s.id));

    setSavedSegments(nextSavedSegments);
    setDraftItems((prev) => prev.filter((d) => !idSet.has(d.id)));
    setSelectedIds(new Set());
    setPendingLabel(null);

    await persistSegments(nextSavedSegments);
  };

  const commitPolygonEdit = (next: Segment) => {
    if (!editingTarget) return;
    if (editingTarget.source === "draft") {
      setDraftItems((prev) => prev.map((d) => (d.id === editingTarget.id ? { ...d, polygons: next } : d)));
    } else {
      setSavedSegments((prev) => prev.map((s) => (s.id === editingTarget.id ? { ...s, polygons: next } : s)));
    }
  };

  const handleBoxMouseMove = (e: React.MouseEvent) => {
    if (!magnifierEnabled) return;
    const rect = imageBoxRef.current?.getBoundingClientRect();
    if (!rect) return;
    lensRef.current?.show(e.clientX - rect.left, e.clientY - rect.top);
  };

  const handleBoxMouseLeave = () => lensRef.current?.hide();

  // 렌즈/팝오버 clamp용 실제 박스 크기. 원본 패널은 600 고정이 아니라서 진입 시 측정
  const handleBoxMouseEnter = () => {
    const rect = imageBoxRef.current?.getBoundingClientRect();
    if (rect && (rect.width !== boxDims.w || rect.height !== boxDims.h)) setBoxDims({ w: rect.width, h: rect.height });
  };

  const renderScene = (options?: { withRef?: boolean }) => (
    <>
      <img
        id="image-part"
        ref={options?.withRef ? imgRef : undefined}
        src={filteredSrc ?? imageData}
        onClick={toolMode === "select" ? onImageClick : undefined}
        onLoad={(e) => setNatural({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight })}
        style={{ width: "100%", height: "100%", objectFit: "contain", cursor: "crosshair" }}
        draggable={false}
      />
      {natural &&
        draftItems.map((item) => (
          <PolygonOverlay
            key={item.id}
            polygons={item.polygons}
            naturalWidth={natural.w}
            naturalHeight={natural.h}
            kind="draft"
            isSelected={selectedIds.has(item.id)}
          />
        ))}
      {natural &&
        visibleSavedSegments.map((s) => (
          <PolygonOverlay
            key={s.id}
            polygons={s.polygons}
            naturalWidth={natural.w}
            naturalHeight={natural.h}
            kind="saved"
            label={s.label}
            isSelected={selectedIds.has(s.id)}
          />
        ))}
      {natural && selectedIds.size > 0 && (
        <SelectionHighlight
          natural={natural}
          items={[...draftItems, ...visibleSavedSegments].filter((it) => selectedIds.has(it.id))}
        />
      )}
    </>
  );

  const renderInteractiveBox = (style: React.CSSProperties) => (
    <div
      ref={imageBoxRef}
      onMouseMove={handleBoxMouseMove}
      onMouseEnter={handleBoxMouseEnter}
      onMouseLeave={handleBoxMouseLeave}
      // 우클릭은 이미지뿐 아니라 꼭짓점(EditableVertexOverlay) 위에서도 라벨 popover를 띄우도록 박스 전체에서 처리
      onContextMenu={onImageContextMenu}
      // 우클릭(button 2)으로 꼭짓점 드래그/lasso가 시작되지 않도록 capture 단계에서 차단
      onPointerDownCapture={(e) => {
        if (e.button === 2) e.stopPropagation();
      }}
      onPointerDown={toolMode === "lasso" ? handlePointerDown : undefined}
      onPointerMove={toolMode === "lasso" ? handlePointerMove : undefined}
      onPointerUp={toolMode === "lasso" ? handlePointerUp : undefined}
      style={{ position: "relative", ...style }}
    >
      {renderScene({ withRef: true })}
      {natural && editingTarget && editingPolygons && (
        <EditableVertexOverlay
          polygons={editingPolygons}
          naturalWidth={natural.w}
          naturalHeight={natural.h}
          imgEl={imgRef.current}
          onChange={commitPolygonEdit}
          onDragEnd={handleVertexDragEnd}
        />
      )}
      {toolMode === "lasso" && natural && (
        <LassoDrawOverlay points={drawingPoints} naturalWidth={natural.w} naturalHeight={natural.h} />
      )}
      {magnifierEnabled && natural && (
        <MagnifierLens ref={lensRef} boxWidth={boxDims.w} boxHeight={boxDims.h} zoom={2.5}>
          {renderScene({ withRef: false })}
          {editingTarget && editingPolygons && (
            <EditableVertexOverlay
              polygons={editingPolygons}
              naturalWidth={natural.w}
              naturalHeight={natural.h}
              imgEl={imgRef.current}
              onChange={commitPolygonEdit}
              onDragEnd={handleVertexDragEnd}
              interactive={false}
            />
          )}
        </MagnifierLens>
      )}
      {pendingLabel && (
        <LabelPopover
          anchor={pendingLabel.anchor}
          boxWidth={boxDims.w}
          boxHeight={boxDims.h}
          initialValue={(() => {
            const labels = savedSegments.filter((s) => pendingLabel.targetIds.includes(s.id)).map((s) => s.label);
            const uniq = Array.from(new Set(labels));
            return uniq.length === 1 ? uniq[0] : "";
          })()}
          showDelete
          suggestions={projectClasses}
          onSubmit={commitLabel}
          onDelete={deleteLabel}
          onCancel={() => setPendingLabel(null)}
        />
      )}
    </div>
  );

  return (
    <div className="flex flex-col h-full w-full border-r border-gray-300">
      <Box
        sx={{
          height: 40,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid",
          borderColor: "grey.300",
          px: 1.25,
          flexShrink: 0,
        }}
      >
        {projectConfig ? (
          <>
            <Box id="labeling-btn" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {projectData?.step !== "검사" &&
                projectData?.class.map((labelClass, index) => (
                  <LabelButton
                    key={index}
                    class={labelClass}
                    configNumber={projectConfig.keyConfig.labeling[index]}
                    registerClick={(fn) => (labelButtonRefs.current[index] = fn)}
                  />
                ))}
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", ml: "auto" }}>
              {imageViewMode !== "original" && (
                <Box
                  onClick={handleShowRawImage}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                    px: 1.25,
                    py: 0.4,
                    borderRadius: "20px",
                    border: "0.5px solid",
                    borderColor: openOrgImage ? "primary.main" : "grey.300",
                    bgcolor: openOrgImage ? "primary.main" : "transparent",
                    color: openOrgImage ? "#fff" : "text.secondary",
                    fontSize: 12,
                    fontWeight: 500,
                    cursor: "pointer",
                    userSelect: "none",
                    transition: "all 0.15s ease",
                    "&:hover": {
                      borderColor: openOrgImage ? "primary.dark" : "grey.400",
                      bgcolor: openOrgImage ? "primary.dark" : "grey.50",
                    },
                  }}
                >
                  {openOrgImage ? <ImageIcon size={13} /> : <Layers size={13} />}
                  {t("Original")} {openOrgImage ? t("On") : t("Off")}
                </Box>
              )}
            </Box>
            <div style={{ display: "flex", gap: 2, background: "#f5f5f7", borderRadius: 8, padding: 2 }}>
              {(["select", "lasso"] as const).map((mode) => (
                <button
                  key={mode}
                  onClick={() => setToolMode(mode)}
                  style={{
                    padding: "4px 10px",
                    fontSize: 8,
                    fontWeight: toolMode === mode ? 600 : 400,
                    color: toolMode === mode ? "#fff" : "#1d1d1f",
                    background: toolMode === mode ? "#2563eb" : "transparent",
                    border: "none",
                    borderRadius: 6,
                    cursor: "pointer",
                    height: 28,
                  }}
                >
                  {mode === "select" ? <Scan size={16} /> : <Lasso size={16} />}
                </button>
              ))}
            </div>
            <CustomToggle checked={magnifierEnabled} onChange={setMagnifierEnabled} label={t("Magnifier")} />
            <LabelFilterSelect
              labels={uniqueLabels}
              counts={labelCounts}
              totalCount={savedSegments.length}
              activeLabel={activeLabelFilter}
              onChange={setActiveLabelFilter}
            />
          </>
        ) : null}
      </Box>
      <div
        className="flex-1 min-h-0 min-w-0 flex items-center justify-center"
        style={{ backgroundColor: "var(--table-row-hover-bg)" }}
      >
        {imageData && (
          <div style={{ position: "relative", width: "80%", height: "80%" }}>
            {projectData?.step === "분석" && (
              <LabelScorePanel
                labels={labels}
                scoreList={scoreList}
                thresholdList={thresholdList}
                visibleIndex={visibleIndex}
                onChangeVisible={setVisibleIndex}
              />
            )}
            {isImageLoading && (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  zIndex: 10,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: "rgba(255,255,255,0.65)",
                  backdropFilter: "blur(3px)",
                  borderRadius: 1,
                  gap: 1,
                }}
              >
                <CircularProgress size={28} thickness={4} />
                <Typography variant="caption" color="text.secondary">
                  {t("LoadingImageMsg")}
                </Typography>
              </Box>
            )}
            <div
              style={{
                width: "100%",
                height: "100%",
                display: "flex",
                flexDirection: "column",

                gap: "8px",
                opacity: isImageLoading ? 0.4 : 1,
                transition: "opacity 0.2s ease",
              }}
            >
              {prepStatus === "preparing" && (
                <Box
                  sx={{
                    position: "fixed",
                    bottom: 24,
                    left: "50%",
                    transform: "translateX(-50%)",
                    backgroundColor: "#1e1e2e",
                    color: "#fff",
                    px: 2,
                    py: 1,
                    borderRadius: 2,
                    fontSize: "13px",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    boxShadow: 4,
                    zIndex: 9999,
                  }}
                >
                  <CircularProgress size="14px" sx={{ color: "#fff" }} />
                  <Typography sx={{ fontSize: "13px", color: "#fff" }}>{t("ScanningImageMsg")}</Typography>
                </Box>
              )}
              {imageViewMode === "original" &&
                renderInteractiveBox({ width: size, height: size, maxWidth: "100%", maxHeight: "100%" })}
              {imageViewMode !== "original" && (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    width: "100%",
                    height: "100%",
                    gap: "12px",
                    overflow: "hidden",
                  }}
                >
                  {/* 원본 이미지 */}
                  {openOrgImage && (
                    <div
                      style={{
                        flex: 1,
                        minWidth: 0,
                        minHeight: 0,
                        display: "flex",
                        flexDirection: "column",
                        overflow: "hidden",
                        gap: 4,
                      }}
                    >
                      <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0, textAlign: "center" }}>
                        {t("Original")}
                      </Typography>

                      <div style={{ flex: 1, minHeight: 0, display: "flex" }}>
                        {renderInteractiveBox({ width: "100%", height: "100%" })}
                      </div>
                    </div>
                  )}
                  <div
                    style={{
                      flex: 1,
                      minWidth: 0,
                      minHeight: 0,
                      display: "flex",
                      flexDirection: "column",
                      overflow: "hidden",
                      gap: 4,
                    }}
                  >
                    {error && !loading && (
                      <Box
                        sx={{
                          flex: 1,
                          minHeight: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          p: 2,
                          border: "1px dashed",
                          borderColor: "divider",
                          borderRadius: 1,
                        }}
                      >
                        <Box sx={{ textAlign: "center" }}>
                          <Typography variant="body2" color="error">
                            {error.message}
                          </Typography>
                          {/* 학습 중에는 AI 서버가 학습에 매여 있어 히트맵이 안 나올 수 있다 */}
                          {isTraining && imageViewMode === "scoremap" && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                              {t("TrainingHeatmapUnstableMsg")}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    )}

                    {imageViewMode === "preview" && !error && processImage && (
                      <>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ flexShrink: 0, textAlign: "center" }}
                        >
                          {t("Preprocessing")}
                        </Typography>
                        <div style={{ flex: 1, minHeight: 0, minWidth: 0, position: "relative" }}>
                          <img
                            src={processImage}
                            style={{
                              position: "absolute",
                              inset: 0,
                              width: "100%",
                              height: "100%",
                              objectFit: "contain",
                            }}
                          />
                        </div>
                      </>
                    )}

                    {imageViewMode === "scoremap" && !error && (
                      <>
                        <Box
                          sx={{
                            flexShrink: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 0.75,
                          }}
                        >
                          <Typography variant="caption" color="text.secondary">
                            {t("Heatmap")}
                          </Typography>
                          {/* 어떤 모델이 뽑은 히트맵인지 항상 보여준다 */}
                          {heatmapModel && (
                            <Tooltip title={t("HeatmapSourceMsg")} arrow>
                              <Box
                                component="span"
                                sx={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                  px: 0.75,
                                  py: 0.15,
                                  borderRadius: "10px",
                                  bgcolor: "rgba(59,130,246,0.08)",
                                  color: "#2563eb",
                                  fontSize: 11,
                                  fontWeight: 600,
                                }}
                              >
                                {heatmapModel.label}
                              </Box>
                            </Tooltip>
                          )}
                        </Box>
                        <div
                          style={{
                            flex: 1,
                            minHeight: 0,
                            width: "100%",
                            overflow: "hidden",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          {!scoremap && (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              {(loading || ((heatmapModel || isMulti) && !heatmapModuleMsg)) && (
                                <CircularProgress size={14} />
                              )}
                              <Typography variant="caption" color="text.secondary" sx={{ textAlign: "center" }}>
                                {!heatmapModel && !isMulti
                                  ? t("NoHeatmapModelMsg")
                                  : isValidating
                                    ? t("ValidatingHeatmapMsg")
                                    : (heatmapModuleMsg ?? t("LoadingModelsMsg"))}
                              </Typography>
                            </Box>
                          )}
                          {imageViewMode === "scoremap" && originImage && scoremap && (
                            <Overlay
                              scoremapImages={scoreMapImage}
                              baseImage={imageData}
                              blend="max"
                              opacity={0.5}
                              lens={magnifierEnabled}
                              lensSize={180}
                              zoom={2.5}
                              zoomMode="lens"
                              lensContentOpacity={1.0}
                              lensSource="composite"
                              enableZoom={magnifierEnabled}
                            />
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
